import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";

export default function LoginForm() {
  const [showPassword, setShowPassword] = useState(false);
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const email = String(data.get("email") || "").trim();
    const password = String(data.get("password") || "");

    if (!email || !password) {
      setError("Please enter your email and password to continue.");
      return;
    }

    setSubmitting(true);
    setError("");

    window.setTimeout(() => {
      setSubmitting(false);
      setError("Incorrect email or password. Please try again.");
    }, 700);
  };

  return (
    <div className="w-full max-w-md">
      <p className="text-[11px] uppercase tracking-[0.3em] text-accent-600">Members Portal</p>
      <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">Sign In</h1>
      <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
        Enter the email and password associated with your club membership.
      </p>

      {error && (
        <div
          role="alert"
          className="mt-6 flex items-start gap-3 rounded-lg border border-accent-300/70 bg-accent-100/70 px-4 py-3"
        >
          <span className="w-5 h-5 shrink-0 flex items-center justify-center text-accent-700 mt-0.5">
            <i className="ri-error-warning-line text-base" aria-hidden="true" />
          </span>
          <p className="text-sm text-accent-900">{error}</p>
        </div>
      )}

      <form id="member-login" className="mt-6 space-y-5" onSubmit={handleSubmit} noValidate>
        <div>
          <label htmlFor="email" className="block text-xs uppercase tracking-[0.18em] text-foreground-600 mb-2">
            Email
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            className="w-full rounded-md border border-background-300/80 bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-colors"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="password" className="block text-xs uppercase tracking-[0.18em] text-foreground-600">
              Password
            </label>
            <Link
              to="/members"
              className="text-xs text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="Enter your password"
              className="w-full rounded-md border border-background-300/80 bg-background-50 pl-4 pr-12 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-400/30 transition-colors"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-1 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-foreground-500 hover:text-foreground-800 cursor-pointer transition-colors"
            >
              <i className={showPassword ? "ri-eye-off-line text-lg" : "ri-eye-line text-lg"} aria-hidden="true" />
            </button>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            name="remember"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="w-4 h-4 rounded border-background-400 text-primary-600 focus:ring-primary-400/40 cursor-pointer"
          />
          <span className="text-sm text-foreground-700">Keep me signed in on this device</span>
        </label>

        <button
          type="submit"
          disabled={submitting}
          className="w-full inline-flex items-center justify-center gap-2 rounded-md bg-primary-600 px-6 py-3 text-sm whitespace-nowrap text-background-50 hover:bg-primary-700 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer transition-colors"
        >
          {submitting ? (
            <>
              <i className="ri-loader-4-line animate-spin text-base" aria-hidden="true" />
              Signing in…
            </>
          ) : (
            <>
              Sign In
              <i className="ri-arrow-right-line text-base" aria-hidden="true" />
            </>
          )}
        </button>
      </form>

      <div className="mt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-foreground-600 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Back to Hillsborough Garden Club
        </Link>
      </div>
    </div>
  );
}